import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

import { Badge } from './ui/badge';
import { SuiFrenPortrait } from './suifren/SuiFrenPortrait';
import { SAINT_PATRICK_EVENT_CFG } from '@/configs/saint-patrick-event-config';
import { campaignRepository } from '@/repositories/campaign-repository';
import { cn, shortenHex } from '@/utils';
import { INTEGER_FORMATTER } from '@/utils/format-date-range';

const STREAK_LEADER_REFRESH_MS = 5 * 60_000;

type SaintPatrickStreakLeaderCardVariant = 'modal' | 'sidebar';

interface SaintPatrickStreakLeaderCardProps {
	variant?: SaintPatrickStreakLeaderCardVariant;
	className?: string;
	onClick?: () => void;
}

export function SaintPatrickStreakLeaderCard({
	variant = 'modal',
	className,
	onClick,
}: SaintPatrickStreakLeaderCardProps) {
	const isSidebar = variant === 'sidebar';
	const streakLeaderQuery = useQuery({
		queryKey: ['saint-patrick', 'streak-leader'],
		queryFn: () => campaignRepository.getStreakLeaderSummary(SAINT_PATRICK_EVENT_CFG.slug),
		enabled: SAINT_PATRICK_EVENT_CFG.state === 'live',
		staleTime: STREAK_LEADER_REFRESH_MS,
		refetchInterval: STREAK_LEADER_REFRESH_MS,
	});

	const streakLeaderName =
		streakLeaderQuery.data?.resolvedName ??
		(streakLeaderQuery.data?.address ? shortenHex(streakLeaderQuery.data.address) : null);
	const streakLeaderWins = streakLeaderQuery.data?.bestStreak ?? 0;
	const streakLeaderSeed = streakLeaderQuery.data?.address ?? streakLeaderQuery.data?.resolvedName ?? null;

	const baseClassName = isSidebar
		? 'group relative w-full overflow-hidden rounded-2xl border border-success/20 bg-[radial-gradient(circle_at_top_left,rgba(115,179,53,0.22),transparent_52%),linear-gradient(135deg,rgba(255,255,255,0.05),rgba(6,28,14,0.18))] p-3 text-left shadow-sm transition-colors hover:border-success/35 hover:bg-[radial-gradient(circle_at_top_left,rgba(115,179,53,0.28),transparent_52%),linear-gradient(135deg,rgba(255,255,255,0.06),rgba(6,28,14,0.24))]'
		: 'rounded-3xl border border-border/60 bg-card/70 p-4 shadow-sm';

	const content = (
		<>
			{isSidebar ? (
				<>
					<img
						src={SAINT_PATRICK_EVENT_CFG.artwork.buttonIcon}
						alt=""
						className="pointer-events-none absolute -bottom-2 -left-2 w-12 -rotate-[18deg] opacity-10"
					/>
					<img
						src={SAINT_PATRICK_EVENT_CFG.artwork.buttonAccent}
						alt=""
						className="pointer-events-none absolute right-3 top-2 w-5 opacity-25 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
					/>
				</>
			) : null}
			<div className="relative flex flex-wrap items-center justify-between gap-3">
				<div className="min-w-0 flex-1">
					<p
						className={cn(
							'font-semibold uppercase tracking-[0.18em] text-muted-foreground',
							isSidebar ? 'text-[10px]' : 'text-[11px]',
						)}
					>
						Current longest streak leader
					</p>
					{streakLeaderQuery.isLoading && !streakLeaderQuery.data ? (
						<div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
							<Loader2 className="h-3.5 w-3.5 animate-spin" />
							<span>Loading leader...</span>
						</div>
					) : (
						<div className="mt-1 flex items-center gap-2">
							{streakLeaderSeed ? (
								<SuiFrenPortrait
									seed={streakLeaderSeed}
									size="custom"
									className={cn(
										'border border-border/70 bg-card/80',
										isSidebar ? 'size-8' : 'size-9',
									)}
									frame="circle"
									zoom={1.55}
									focusOffset={{ y: -18 }}
									showShadow
								/>
							) : null}
							<p className={cn('truncate font-semibold text-foreground', isSidebar ? 'text-[13px]' : 'text-sm')}>
								{streakLeaderName ?? 'No streak posted yet'}
							</p>
						</div>
					)}
				</div>
				<Badge variant={streakLeaderWins > 0 ? 'primary' : 'outline'} className="shrink-0">
					{streakLeaderWins > 0 ? `${INTEGER_FORMATTER.format(streakLeaderWins)} wins` : 'Waiting...'}
				</Badge>
			</div>
			<p className={cn('mt-1 text-muted-foreground/55', isSidebar ? 'text-[9px]' : 'text-[10px]')}>Updates every 5 min.</p>
		</>
	);

	if (onClick) {
		return (
			<button type="button" className={cn(baseClassName, className)} onClick={onClick}>
				{content}
			</button>
		);
	}

	return <div className={cn(baseClassName, className)}>{content}</div>;
}
